import React from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, UtensilsCrossed, Flame } from "lucide-react";

const TYPE_KEYS = { fixed: "ieTypeFixed", removable: "ieTypeRemovable", duplicable: "ieTypeDuplicable" };

const emptyIngredient = () => ({ name: "", type: "removable", levels: [] });

/**
 * Éditeur de composition d'un plat (côté restaurant).
 * Chaque ingrédient est fixe, retirable (sans frais) ou duplicable (payant, par niveaux).
 * Props: ingredients, onChange(ingredients), cooking {choices, default}, onCookingChange({choices, default})
 */
export default function IngredientsEditor({ ingredients = [], onChange, cooking = { choices: [], default: "" }, onCookingChange }) {
  const { t } = useTranslation();
  const patch = (i, p) => onChange(ingredients.map((x, k) => (k === i ? { ...x, ...p } : x)));
  const remove = (i) => onChange(ingredients.filter((_, k) => k !== i));
  const patchLevel = (i, li, p) => patch(i, { levels: ingredients[i].levels.map((l, k) => (k === li ? { ...l, ...p } : l)) });

  const cookingEnabled = (cooking.choices || []).length > 0;

  return (
    <div className="space-y-4">
      {/* Ingrédients */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <UtensilsCrossed className="w-4 h-4 text-brand-red" />
          <span className="text-xs uppercase tracking-widest text-mute font-semibold">{t("rdash.ieTitle")}</span>
        </div>
        {ingredients.length === 0 && (
          <p className="text-xs text-mute mb-2">{t("rdash.ieDesc")}</p>
        )}
        <div className="space-y-2">
          {ingredients.map((ing, i) => (
            <div key={i} className="rounded-xl border border-sand bg-elevated/50 p-2.5" data-testid={`ing-row-${i}`}>
              <div className="flex items-center gap-2">
                <input
                  value={ing.name}
                  onChange={(e) => patch(i, { name: e.target.value })}
                  placeholder={t("rdash.ieNamePh")}
                  maxLength={40}
                  data-testid={`ing-name-${i}`}
                  className="flex-1 min-w-0 bg-white border border-sand rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-brand-red"
                />
                <select
                  value={ing.type}
                  onChange={(e) => patch(i, { type: e.target.value, levels: e.target.value === "duplicable" && !ing.levels?.length ? [{ label: "Double", price_delta: 0 }] : ing.levels })}
                  data-testid={`ing-type-${i}`}
                  className="bg-white border border-sand rounded-lg px-2 py-1.5 text-xs outline-none shrink-0"
                >
                  {Object.entries(TYPE_KEYS).map(([v, k]) => <option key={v} value={v}>{t(`rdash.${k}`)}</option>)}
                </select>
                <button type="button" onClick={() => remove(i)} data-testid={`ing-del-${i}`} className="p-1 rounded text-brand-red hover:bg-white shrink-0" aria-label="Supprimer"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              {ing.type === "duplicable" && (
                <div className="mt-2 pl-2 space-y-1.5">
                  {(ing.levels || []).map((lv, li) => (
                    <div key={li} className="flex items-center gap-2">
                      <input
                        value={lv.label}
                        onChange={(e) => patchLevel(i, li, { label: e.target.value })}
                        placeholder={li === 0 ? "Double" : "Triple"}
                        maxLength={20}
                        data-testid={`ing-level-label-${i}-${li}`}
                        className="w-24 bg-white border border-sand rounded-lg px-2 py-1 text-xs outline-none"
                      />
                      <span className="text-[10px] text-mute">+</span>
                      <input
                        value={lv.price_delta || ""}
                        onChange={(e) => patchLevel(i, li, { price_delta: Number(e.target.value.replace(/[^0-9]/g, "")) || 0 })}
                        inputMode="numeric"
                        placeholder="0"
                        data-testid={`ing-level-price-${i}-${li}`}
                        className="w-20 bg-white border border-sand rounded-lg px-2 py-1 text-xs outline-none text-right"
                      />
                      <span className="text-[10px] text-mute">GNF</span>
                      <button type="button" onClick={() => patch(i, { levels: ing.levels.filter((_, k) => k !== li) })} className="p-0.5 text-mute hover:text-brand-red" aria-label="Supprimer le niveau"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => patch(i, { levels: [...(ing.levels || []), { label: "", price_delta: 0 }] })} data-testid={`ing-add-level-${i}`} className="text-[11px] font-semibold text-brand-red inline-flex items-center gap-1 hover:underline">
                    <Plus className="w-3 h-3" /> {t("rdash.ieAddLevel")}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={() => onChange([...ingredients, emptyIngredient()])} data-testid="ing-add" className="mt-2 rounded-full border border-sand hover:border-brand-red px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1">
          <Plus className="w-3 h-3" /> {t("rdash.ieAddIng")}
        </button>
      </div>

      {/* Cuisson */}
      <div className="pt-3 border-t border-sand">
        <label className="flex items-center gap-2 cursor-pointer select-none mb-2">
          <input
            type="checkbox"
            checked={cookingEnabled}
            onChange={(e) => onCookingChange(e.target.checked ? { choices: ["Saignant", "À point", "Bien cuit"], default: "À point" } : { choices: [], default: "" })}
            data-testid="cooking-toggle"
            className="w-4 h-4"
          />
          <Flame className="w-4 h-4 text-brand-red" />
          <span className="text-xs uppercase tracking-widest text-mute font-semibold">{t("rdash.ieCookingTitle")}</span>
        </label>
        {cookingEnabled && (
          <div className="space-y-1.5 pl-6">
            {cooking.choices.map((c, ci) => (
              <div key={ci} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="cooking-default"
                  checked={cooking.default === c}
                  onChange={() => onCookingChange({ ...cooking, default: c })}
                  data-testid={`cooking-default-${ci}`}
                  title={t("rdash.ieCookingDefault")}
                  className="w-3.5 h-3.5"
                />
                <input
                  value={c}
                  onChange={(e) => {
                    const choices = cooking.choices.map((x, k) => (k === ci ? e.target.value : x));
                    onCookingChange({ choices, default: cooking.default === c ? e.target.value : cooking.default });
                  }}
                  maxLength={30}
                  data-testid={`cooking-choice-${ci}`}
                  className="flex-1 min-w-0 bg-white border border-sand rounded-lg px-2.5 py-1.5 text-sm outline-none"
                />
                <button type="button" onClick={() => onCookingChange({ choices: cooking.choices.filter((_, k) => k !== ci), default: cooking.default === c ? "" : cooking.default })} className="p-1 text-mute hover:text-brand-red" aria-label="Supprimer"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button type="button" onClick={() => onCookingChange({ ...cooking, choices: [...cooking.choices, ""] })} data-testid="cooking-add-choice" className="text-[11px] font-semibold text-brand-red inline-flex items-center gap-1 hover:underline">
              <Plus className="w-3 h-3" /> {t("rdash.ieAddChoice")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
